/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
// eslint-disable-next-line @typescript-eslint/no-use-before-define
import { Button, Icon, Popup, RadioButton, TextInput } from 'lego-on-react';
import React, { useCallback, useRef, useState } from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/DraftControlPanel';
import block from 'propmods';

import { devicesTestingApi } from '../../../api';
import { useCurrentAliceApp } from '../../../hooks/useCurrentAliceApp';
import { useFetchingHandler } from '../../../hooks/useFetchingHandler';
import { useFormSettings } from '../../../hooks/useFormSettings';
import { moment } from '../../../lib/moment';
import {
    DevicesTestingFormSettings,
    DevicesTestingType,
    Range,
    serializeDevicesTestingOptions,
} from '../../../model/devicesTestingForm';
import { getBaseTooltipReducer } from '../../../reducers';
import { getIsYangoRequest } from '../../../utils';
import { formatDevicesTestingRange, generateTestingHours } from '../../../utils/time';
import { ActionButton } from '../../ActionButton/ActionButton';
import { FormCheckbox } from '../../Form/Form';
import TextArea from '../../Form/TextArea';
import { Note } from '../../Note/Note';
import { Select } from '../../Select/Select';
import { getOnlineDevicesTestingNoteText } from '../text';

import './TestingDateSelectionForm.scss';

const b = block('TestingDateSelectionForm');
const t = i18n(keyset);

interface Props {
    afterSave?: () => Promise<void>;
    onCancel?: () => void;
    initialSettings: DevicesTestingFormSettings;
    disabled?: boolean;
}

interface TestingDateSelectProps {
    onSelect: (_range: Range) => void;
    range?: Range;
    disabled?: boolean;
}

const generateInputName = (name: string) => {
    return `paskills-testing_date-${name}`;
};

const TestingDateSelect: React.FC<TestingDateSelectProps> = ({ onSelect, range, disabled }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const [popupIsVisible, setPopupIsVisible] = useState(false);

    const [testingDateRanges, testingDateOrder] = generateTestingHours(moment());

    return (
        <>
            <div {...b('date')} ref={containerRef}>
                <TextInput
                    name={generateInputName('date')}
                    theme="normal"
                    size="s"
                    iconRight={<Icon type="calendar" size="s" style={{ width: '28px' }} />}
                    placeholder={`* ${t('date-time')}`}
                    text={range ? formatDevicesTestingRange(range, true) : ''}
                    onFocus={() => !disabled && setPopupIsVisible(true)}
                    autocomplete={false}
                    disabled={disabled}
                />
            </div>
            <Popup
                autoclosable
                theme="normal"
                target="anchor"
                visible={popupIsVisible && !disabled}
                anchor={() => containerRef.current}
                onOutsideClick={() => setPopupIsVisible(false)}
                directions={['bottom-right']}
            >
                <div {...b('date-options')}>
                    {testingDateOrder.map((formattedDate, i) => {
                        const ranges = testingDateRanges[formattedDate];

                        return (
                            <div {...b('date-options-group')} key={i}>
                                <div {...b('date-options-title')}>{formattedDate}</div>
                                {ranges.map((r, j) => {
                                    const onClick = () => {
                                        if (disabled) return;
                                        onSelect(r);
                                        setPopupIsVisible(false);
                                    };

                                    return (
                                        <div {...b('date-option')} key={j} onClick={onClick}>
                                            {formatDevicesTestingRange(r)}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </Popup>
        </>
    );
};

const LANG_SELECT_OPTIONS = [
    { value: 'ru', text: t('rus') },
    { value: 'en', text: t('eng') },
];

export const TestingDateSelectionForm: React.FC<Props> = ({ afterSave, onCancel, initialSettings, disabled }) => {
    const skill = useCurrentAliceApp()!;
    const [isChanged, setIsChanges] = useState(false);
    const { settings, onChange } = useFormSettings({
        defaultSettings: initialSettings,
        registerChanges: setIsChanges,
    });

    const [isDevicesTransferConfirmed, setIsDevicesTransferConfirmed] = useState(false);

    const isFormSettingsSet =
        settings.login &&
        settings.password &&
        settings.selectedDateRange &&
        (settings.type === 'skype' ? settings.skypeLogin : true);

    const isSavingAllowed = isFormSettingsSet && isChanged;

    const [onSave, savingState] = useFetchingHandler({
        handler: async () => {
            const res = await devicesTestingApi.saveDevicesTestingOptions(
                skill.id,
                serializeDevicesTestingOptions(settings),
            );
            await afterSave?.();

            return res;
        },
    });

    const isSaving = savingState === 'loading';

    const onLangChange = useCallback((lang: string) => onChange('lang')(lang), [onChange]);

    return (
        <div {...b()}>
            <div {...b('type')}>
                <RadioButton
                    name="type"
                    theme="normal"
                    size="s"
                    view="default"
                    value={settings.type}
                    onChange={(e) => onChange('type')(e.target.value as DevicesTestingType)}
                    disabled={disabled}
                >
                    <RadioButton.Radio value="zoom">{t('meeting-zoom')}</RadioButton.Radio>
                    <RadioButton.Radio value="skype">{t('meeting-skype')}</RadioButton.Radio>
                </RadioButton>
            </div>
            {settings.type === 'zoom' || settings.type === 'skype' ? (
                <div {...b('fields')}>
                    <div {...b('field')}>
                        <TestingDateSelect
                            range={settings.selectedDateRange}
                            onSelect={onChange('selectedDateRange')}
                            disabled={disabled}
                        />
                    </div>
                    <div {...b('field')}>
                        <TextInput
                            name={generateInputName('provider_login')}
                            theme="normal"
                            size="s"
                            text={settings.login}
                            onChange={onChange('login')}
                            autocomplete={false}
                            placeholder={`* ${t('meeting-provider-login')}`}
                            disabled={disabled}
                        />
                    </div>
                    <div {...b('field')}>
                        {/* Обертка в виде формы нужна, чтобы предотвратить автоподстановку доменных паролей*/}
                        <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
                            <TextInput
                                name={generateInputName('provider_password')}
                                theme="normal"
                                size="s"
                                text={settings.password}
                                type="password"
                                onChange={onChange('password')}
                                autocomplete={false}
                                placeholder={`* ${t('meeting-provider-password')}`}
                                disabled={disabled}
                            />
                        </form>
                    </div>
                    {settings.type === 'skype' && (
                        <div {...b('field')}>
                            <TextInput
                                name={generateInputName('skype_login')}
                                theme="normal"
                                size="s"
                                text={settings.skypeLogin}
                                onChange={onChange('skypeLogin')}
                                placeholder={`* ${t('meeting-skype-login')}`}
                                disabled={disabled}
                            />
                        </div>
                    )}
                    <div {...b('field')}>
                        <TextArea
                            name={generateInputName('comment')}
                            text={settings.comment!}
                            rows={2}
                            onChange={onChange('comment')}
                            autosized={false}
                            placeholder={t('comment')}
                            disabled={disabled}
                        />
                    </div>
                    <div {...b('field', { lang: true })}>
                        <Select
                            name={generateInputName('lang')}
                            namespace={generateInputName('lang')}
                            value={settings.lang}
                            onChange={onLangChange as any}
                            type="radio"
                            placeholder={t('lang')}
                            options={LANG_SELECT_OPTIONS}
                            disabled={disabled}
                        />
                        <span {...b('clarification')}>{t('meeting-choose-lang')}</span>
                    </div>
                    <div {...b('field')}>
                        <Note type="info" hideLabel>
                            <div {...b('note')}>{getOnlineDevicesTestingNoteText()}</div>
                        </Note>
                    </div>
                </div>
            ) : (
                <div {...b('checkbox')}>
                    <FormCheckbox
                        checked={isDevicesTransferConfirmed}
                        onChange={() => setIsDevicesTransferConfirmed(!isDevicesTransferConfirmed)}
                        multiline
                        centered={false}
                        disabled={disabled}
                    >
                        {t(getIsYangoRequest() ? 'meeting-devices-sent-yango' : 'meeting-devices-sent-yandex')}
                    </FormCheckbox>
                </div>
            )}
            <div {...b('buttons')}>
                <div {...b('button')}>
                    <ActionButton
                        tooltipReducer={getBaseTooltipReducer({ successLabel: '' })}
                        action={async () => {
                            await onSave();
                            return 'clear';
                        }}
                        defaultTooltipState={{ tooltipVisible: false }}
                        disabled={disabled || !isSavingAllowed}
                    >
                        {t('save')}
                    </ActionButton>
                </div>
                {onCancel && (
                    <div {...b('button')}>
                        <Button theme="normal" size="s" onClick={onCancel} disabled={disabled || isSaving}>
                            {t('cancel')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
